import SOrgSelect from "@/components/SOrgSelect";
import { Org } from "@/types/org";
import { AcceptStateEnum, PurchaseOrderItem } from "@/types/purchase";
import { Customer, PriceHis, SaleOrderAdd, SaleOrderAddItem, SaleOrderItemState } from "@/types/sales";
import { getSupplierName } from "@/utils/supplier";
import { Button, Flex, Form, Input, Modal, notification, Spin, Steps, TableProps, Typography } from "antd";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { orgList as orgListApi } from "@/apis/org";
import SelectCustomer from "@/components/Selector/SelectCustomer";
import STableLayout from "@/styles/STableLayout";
import { AnyObject } from "antd/es/_util/type";
import SText from "@/styles/SText";
import { numFormmater } from "@/pages/Requirement/util";
import SaleDetail from "@/pages/Requirement/pages/Detail/components/MultiPurchaseSteps/components/SaleDetail";
import { productList, ProductListObjParams } from "@/apis/product";
import { latestPrices } from "@/apis/customer";
import { useMainCurrency } from "@/layout/hooks/useMainCurrency";
import { divided, dp, multi, plus } from "@/utils/calc";
import { sAdd as saleOrderAdd, update } from "@/apis/order";
import { acceptStateUpdate } from "@/apis/supplier";
import { SConModal } from "@/styles/SConModal";
import { itemUpdateByKey } from "@/apis/purchase";
import { BASEURL } from "@/apis";


interface PurSwapSaleProps {
  open: boolean,
  orders: PurchaseOrderItem[];
  onClose: () => void;
  onSuccess: (saleOrder: SaleOrderItemState) => void;
}
const PurSwapSale: FC<PurSwapSaleProps> = ({ open, orders, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { getMainCurrency } = useMainCurrency();


  const [orgList, setOrgList] = useState<Org[]>([]);
  const [orgId, setOrgId] = useState<string>('');
  const [customer, setCustomer] = useState<Customer | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState<number>(0);
  const [errCodes, setErrCodes] = useState<Array<string>>([]);
  const [resSaleOrder, setResSaleOrder] = useState<SaleOrderItemState | null>(null);
  const [innerNumber, setInnerNumber] = useState<string>('');


  const { supplier } = useMemo(() => {
    let supplier = orders[0].order.supplier;
    return {
      supplier
    }
  }, [orders]);

  useEffect(() => {
    fetchOrgList();
  }, [])

  const fetchOrgList = async () => {
    const supplierApi = supplier?.wmsApi ?? ''
    setOrgId('');
    setOrgList([]);
    setCustomer(null);

    if (!!!supplierApi) {
      notification.error({
        message: t("requirement.supplierApiError"),
        description: `${t("requirement.supplierApiLabel")}${supplierApi}`,
      })
      return;
    }

    setLoading(true);
    try {
      const res = await orgListApi(undefined, supplierApi);
      setOrgList(res.body);
    } catch {
      notification.error({ message: t("requirement.fetchOrgInfoFailed") })
    } finally {
      setLoading(false);
    }
  };

  const handleOrg = (id: string) => {
    setOrgId(id);
    setCustomer(null)
  };

  const handleCustomer = (customerSelected: Customer) => {
    setCustomer(customerSelected)
  };

  const matchData = async () => {
    if (!!!orgId) return notification.error({ message: t('sale.plOrg') });
    if (!!!customer) return notification.error({ message: t('sale.plCustomer') });
    setLoading(true);
    setErrCodes([]);
    try {
      let codes = orders.map(i => i.product!.code);
      const params: ProductListObjParams = {
        categoryId: undefined,
        params: {
          strs: codes,
        },
        wmsApi: supplier.wmsApi
      };
      const { body: supplierProducts } = await productList(params);
      let errProductCodes: string[] = [];
      codes.forEach(code => {
        if (!!!supplierProducts.find(p => p.code === code)) errProductCodes.push(code)
      });
      if (errProductCodes.length > 0) {
        notification.error({ message: t('purchase.notSuppPros') });
        setErrCodes([...errProductCodes]);
        setLoading(false);
        return;
      };
      const { body: priceHisList } = await latestPrices(customer.id, undefined, supplier.wmsApi);
      const priceHisListMap: Record<string, PriceHis> = {};
      priceHisList.forEach(price => priceHisListMap[price.product!.code] = price)
      codes.forEach(code => {
        if (!!!priceHisListMap[code]) errProductCodes.push(code)
      });
      if (errProductCodes.length > 0) {
        notification.error({ message: t('purchase.notSaleProPrice') });
        setErrCodes([...errProductCodes]);
        setLoading(false);
        return;
      };
      const currencyDict = await getMainCurrency(supplier.wmsApi ?? '', []);
      const saleOrderItems: SaleOrderAddItem[] = [];
      orders.forEach(order => {
        const priceHis = priceHisListMap[order.product!.code];
        const quantity = Number(order.count) || 0;
        const price = priceHis.price || '1';
        const amount = multi(quantity + '', price);
        const saleItem: SaleOrderAddItem = {
          priceHisId: priceHis.id,
          price,
          remark: '',
          productName: order.product!.name,
          quantity,
          discountedPrice: '0',
          amount: dp(amount),
          discount: '0',
          realAmount: dp(amount),
          bquantity: 0,
          bdiscountedPrice: '0',
          bamount: '0',
          bdiscount: '0',
          brealAmount: '0',
          totalRealAmount: dp(amount),
        };
        saleOrderItems.push(saleItem);
      });
      let totalRealAmountSum = '0';
      saleOrderItems.forEach(item => {
        totalRealAmountSum = plus(totalRealAmountSum, item.totalRealAmount || '0');
      });

      const amount = divided(totalRealAmountSum, plus('1', divided(customer.taxRate || '0', '100')));
      let orderTotalAmount = '0';
      saleOrderItems.forEach(item => {
        orderTotalAmount = plus(item.totalRealAmount, orderTotalAmount)
      });
      const saleOrderAddData: SaleOrderAdd = {
        docId: '',
        type: '1',
        customerId: customer.id,
        country: customer.country || '',
        taxRate: customer.taxRate || '',
        receiver: customer.shortName || customer.name1 || customer.name2 || customer.code || '',
        receiverContact: customer.contact || '',
        receiverMobile: customer.mobile || '',
        receiverAddress: customer.address || '',
        totalAmount: dp(amount),
        realAmount: amount,
        preApproval: 0,
        innerNumber,
        finishState: 0,
        extPrice: '0',
        items: saleOrderItems,
        shippingMethod: '',
        currency: customer.currency || currencyDict?.key || '2',
        commissionRate: customer.commissionRate ? String(customer.commissionRate) : '',
        otherNumber1: orders.map(o => o.number).join(','),
        productId: null,
        preFinishDate: null,
        reqDeliDate: null,
        smtStartDate: null,
        totalRealAmount: orderTotalAmount,
        mfgr: '',
        customerModel: '',
        urgencyLevel: '',
        specialReq: '',
        sourceOrder: orders.map(oi => oi.number).join(','),
        sourceServer: BASEURL
      };
      let saleOrderRes = await saleOrderAdd(saleOrderAddData, orgId, supplier.wmsApi ?? '');
      notification.success({
        message: t("purchase.purchaseTransferSuccess"),
        description: `${t("requirement.saleOrderNumber")}${saleOrderRes.number}`,
      })
      setResSaleOrder({ ...saleOrderRes });
      for (let j = 0; j < orders.length; j++) {
        try {
          if (orders[j].order.acceptState !== AcceptStateEnum.RESOLVED) await acceptStateUpdate(orders[j]?.orderId ?? '', AcceptStateEnum.RESOLVED)
          await itemUpdateByKey(orders[j].id, { key: "otherNumber1", value: orders[j].otherNumber1 ? `${orders[j].otherNumber1},${saleOrderRes.number}` : saleOrderRes.number })
        } catch (err) { }
      }
      setCurrent(current + 1);
      setLoading(false);
    } catch (error) {
      notification.error({
        message: t('purchase.swapErr')
      })
      setLoading(false);
    }
  };

  const columns: TableProps<PurchaseOrderItem>['columns'] = useMemo(() => [
    {
      title: <SText text={t("purchase.purNo")} />,
      dataIndex: "number",
      width: 180,
      sorter: (a, b) => {
        const valA = a?.number ?? "";
        const valB = b?.number ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => {
        return (
          <Typography.Text
            ellipsis={{ tooltip: item?.number ?? "" }}
          >
            {item?.number ?? ""}
          </Typography.Text>
        );
      },
    },
    {
      title: <SText text={t("product.code")} />,
      dataIndex: ["product", "code"],
      width: 100,
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%", color: errCodes.includes(item.product!.code) ? "red" : "#000" }} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.brand")} />,
      dataIndex: "model1",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 80,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("requirement.saleNum")} />,
      dataIndex: "count",
      width: 80,
      sorter: (a, b) => (a?.count ?? 0) - (b?.count ?? 0),
      render: (_, item) => (
        <SText text={numFormmater(item?.count || 0)} style={{ width: "100%" }} />
      ),
    },
  ], [errCodes]);

  const stepsItems = useMemo(() => [
    {
      title: t("purchase.swapData"),
      content: (
        <STableLayout
          dataSource={orders}
          columns={columns as Array<AnyObject>}
          total={orders.length}
          pagination={false}
          extHeight={100}
          headerChildren={null}
          scrollX={1000}
        />
      ),
    },
    {
      title: t("purchase.swapDone"),
      content: (
        <SaleDetail saleOrder={resSaleOrder} />
      ),
    },
  ], [supplier, orders, resSaleOrder, errCodes]);

  return (
    <Modal title={t('requirement.swapSale')} open={open}
      maskClosable={false} onCancel={onClose}
      width={1200}
      footer={
        <Flex align='center' justify='space-between' style={{ width: "100%" }}>
          {current === 0 ? (
            <SConModal title={t('common.tip')} content={t('requirement.confirmGenerateSaleOrder')} onOk={matchData} >
              <Button type='primary' >{t('purchase.startSwap')}</Button>
            </SConModal>
          ) : <span></span>}
          {current === 1 ? (
            <Button type='primary' onClick={() => onSuccess(resSaleOrder!)}>{t('common.confirm')}</Button>
          ) : <span></span>}
        </Flex>
      }
    >
      <Spin spinning={loading}>
        <Flex vertical style={{ height: "70vh" }} gap={10}>
          <Steps current={current} items={stepsItems} />

          <Flex gap={10} align='flex-end' >
            <Typography.Title level={3} style={{ margin: 0, flex: 1 }} >{t('purchase.supplierPrint')}{getSupplierName(supplier)}</Typography.Title>
            {current === 0 && (
              <Fragment>
                <Typography.Title level={5} style={{ margin: 0 }} >{t('common.org')}：</Typography.Title>
                <SOrgSelect allowClear={false}
                  onChange={handleOrg}
                  value={orgId || undefined}
                  list={orgList}
                  loading={loading}
                  style={{ minWidth: 220 }}
                />
              </Fragment>
            )}
            {!!orgId && supplier && current === 0 && <Fragment>
              <Typography.Title level={5} style={{ margin: 0 }} >{t('sale.customerInfo')}：</Typography.Title>
              <SelectCustomer
                supplierApi={supplier.wmsApi}
                orgId={orgId} disabled={!!!orgId}
                onChangeCustomer={handleCustomer}
                value={customer?.id ?? undefined}
                style={{ minWidth: 220 }}
                defaultList={customer ? [customer] : undefined}
              />
            </Fragment>}
          </Flex>
          {!!orgId && !!customer && current === 0 && <Form.Item label={t('sale.internalNumber')} style={{ marginBottom: 0 }} >
            <Input value={innerNumber} onChange={e => setInnerNumber(e.target.value)}
              placeholder={t('sale.plInnerNumber')}
            />
          </Form.Item>}
          {stepsItems[current].content}
        </Flex>
      </Spin>
    </Modal>
  )
};

export default PurSwapSale;